"use client";
import { layoutStyles } from "./forum-layout.styles";

import type { ForumWikiRailState, ForumLinkRenderer } from "./forum.types";
import { useRef } from "react";
import * as stylex from "@stylexjs/stylex";
import { styles } from "./forum.styles";
import { RefreshFeedback } from "./forum-refresh-feedback";
import { Button } from "@softmax/design-system";

export type ForumWikiRailViewProps = Readonly<{
  state: ForumWikiRailState;
  wikiHref: string;
  onRefresh(): void;
  renderLink: ForumLinkRenderer;
}>;

export function ForumWikiRailView({
  state,
  wikiHref,
  onRefresh,
  renderLink,
}: ForumWikiRailViewProps) {
  const refreshTrigger = useRef<HTMLButtonElement>(null);
  const retry = () => {
    refreshTrigger.current?.focus();
    onRefresh();
  };
  return (
    <section aria-label="The wiki">
      <h2 {...stylex.props(layoutStyles.railHeading)}>The wiki</h2>
      {state.status === "loading" && (
        <output {...stylex.props(layoutStyles.railDescription)}>
          Loading pages…
        </output>
      )}
      {state.status === "error" && (
        <RefreshFeedback
          refreshing={false}
          error={state.message}
          onRefresh={retry}
        />
      )}
      {state.status === "ready" && (
        <>
          <RefreshFeedback
            refreshing={state.refreshing}
            error={state.refreshError}
            onRefresh={retry}
          />
          {state.pages.length === 0 && (
            <p {...stylex.props(layoutStyles.railDescription)}>No pages yet.</p>
          )}
          <ul {...stylex.props(layoutStyles.railList)}>
            {state.pages.slice(0, 6).map((page) => (
              <li key={page.id}>
                {renderLink({
                  href: page.href,
                  ...stylex.props(styles.link),
                  children: page.title,
                })}
              </li>
            ))}
          </ul>
        </>
      )}
      <Button
        ref={refreshTrigger}
        size="sm"
        variant="quiet"
        aria-label="Refresh wiki pages"
        aria-disabled={state.status === "ready" && state.refreshing}
        onClick={() => {
          if (state.status !== "ready" || !state.refreshing) retry();
        }}
      >
        Refresh
      </Button>
      {renderLink({
        href: wikiHref,
        ...stylex.props(styles.link),
        children: "Open the wiki →",
      })}
    </section>
  );
}
